import { usePlacesStore } from "../stores/placesStore";
import { useUrlFilters } from "../lib/useUrlFilters";
import type { Place, OutreachStatus } from "@hsh/shared";
import { FoodHygieneRatingInline } from "./FoodHygieneRatingBadge";

interface PlacesListProps {
  onSelectPlace: (place: Place) => void;
}

export default function PlacesList({ onSelectPlace }: PlacesListProps) {
  const { places, pagination, isLoading } = usePlacesStore();
  const { setPage } = useUrlFilters();

  const getScoreColor = (score: number) => {
    if (score >= 80) return "bg-red-100 text-red-700";
    if (score >= 60) return "bg-orange-100 text-orange-700";
    if (score >= 40) return "bg-yellow-100 text-yellow-700";
    if (score >= 20) return "bg-green-100 text-green-700";
    return "bg-gray-100 text-gray-700";
  };

  const getStatusColor = (status: OutreachStatus) => {
    switch (status) {
      case "not_contacted":
        return "border-gray-300";
      case "contacted":
        return "border-blue-400";
      case "meeting_booked":
        return "border-green-400";
      case "referred":
        return "border-purple-400";
      case "not_suitable":
        return "border-red-400";
      case "follow_up":
        return "border-yellow-400";
      default:
        return "border-gray-300";
    }
  };

  return (
    <div className="flex flex-col h-full">
      {isLoading && (
        <div className="p-4 text-center text-gray-500">Loading...</div>
      )}

      <div className="flex-1 overflow-auto">
        {places.map((place) => (
          <div
            key={place._id}
            onClick={() => onSelectPlace(place)}
            className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors border-l-4 ${getStatusColor(
              place.outreachStatus
            )}`}
          >
            <div className="flex items-start justify-between mb-1">
              <h3 className="font-medium text-gray-900 text-sm">
                {place.name}
              </h3>
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${getScoreColor(
                  place.score
                )}`}
              >
                {place.score}
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              {place.address}, {place.town}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500">{place.category}</span>
              {!place.hasWebsite && !place.website && (
                <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                  No website
                </span>
              )}
              {place.foodHygieneRating && (
                <FoodHygieneRatingInline rating={place.foodHygieneRating} />
              )}
            </div>
          </div>
        ))}

        {places.length === 0 && !isLoading && (
          <div className="p-8 text-center text-gray-500">
            No places found matching your filters
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="p-3 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={() => setPage(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPage(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
